"use client";
import { CheckIcon } from "@cmp-src/icons/icons";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import { Button } from "@cmp-src/primitives/button";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  AiChatToolShowReservationChangeOutput,
  AiChatToolShowReservationChangePart
} from "@gettactic/api";
import { getApiErrorMessage } from "@gettactic/helpers";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useAuthenticated, useTacticHooks } from "@gettactic/hooks";
import { format } from "date-fns";
import React from "react";
import { toast } from "react-hot-toast";
import { MapWithMiniOfficePhoto } from "@/features/dashboard/components/DashboardEventDetails/MapWithMiniOfficePhoto";
import { invalidateReservationQueries } from "@/features/reserve/components/Reserve/helpers";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import {
  buildMovePayload,
  compareRooms,
  hasHalfTimeChange,
  moveMapDay,
  wallClockInTimeZone
} from "@/features/tessa/components/tools/reservation-actions/reservationActions";
import { useSingleFlight } from "@/features/tessa/components/utils/useSingleFlight";
import { useTessaMessageState } from "@/features/tessa/components/utils/useTessaMessageState";
import { TessaMessageRendererProps } from "@/features/tessa/types";
import { api } from "@/lib/api/api";

const timeRange = (start: Date, end: Date) =>
  `${format(start, "EEE, MMM d")} · ${format(start, "p")} – ${format(end, "p")}`;

/** An existing reservation moved to another resource and/or time, confirmed here. */
export const ChatBotToolShowReservationChange: React.FC<
  TessaMessageRendererProps<AiChatToolShowReservationChangePart>
> = ({ part, message }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowReservationChangeOutput
    | undefined;
  const askTessa = useAskTessa();
  const messageState = useTessaMessageState(
    askTessa?.selectedChatId,
    message,
    part
  );
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { queryClient } = useTacticHooks();
  const { run: runOnce, isRunning: isMoving } = useSingleFlight();

  if (!output) return <TacticLoader className={"opacity-60"} />;

  const schedule = output._metadata?.schedules_by_id?.[output.schedule_id];
  if (!schedule) {
    return (
      <ChatBotToolCard toolId={part.toolCallId}>
        This reservation couldn't be found.
      </ChatBotToolCard>
    );
  }

  const newResource = output.resource_id
    ? output._metadata?.resources_by_id?.[output.resource_id]
    : undefined;
  const resource = newResource ?? schedule.resource;
  const rooms = compareRooms(
    schedule.resource,
    newResource,
    output._metadata?.amenities_by_resource_id
  );
  // The reservation's own office (core sends it with the schedule), else one of the user's offices, else the
  // schedule's zone
  const timeZone =
    (schedule as { office?: { time_zone?: string } }).office?.time_zone ||
    authenticatedUser.offices?.byId[schedule.resource?.office_id ?? ""]
      ?.time_zone ||
    schedule.time_zone;
  // Both on the office's clock: the reservation's times are the browser's wall clock with a trailing Z, the
  // proposed ones are the office's wall clock
  const officeClock = (value: string) =>
    new Date(
      dateStringToLocalTime(
        timeZone ? wallClockInTimeZone(value, timeZone) : value
      )
    );
  const from = timeRange(
    officeClock(schedule.start),
    officeClock(schedule.end)
  );
  const to =
    output.start && output.end
      ? timeRange(
          new Date(dateStringToLocalTime(output.start)),
          new Date(dateStringToLocalTime(output.end))
        )
      : from;
  const halfTimes = hasHalfTimeChange(output);
  const isDone = messageState?.state === "completed";

  const move = () => runOnce(moveReservation);

  const moveReservation = async () => {
    try {
      const result = await api.client.schedules.put(
        schedule.id,
        buildMovePayload(schedule, output, timeZone),
        false,
        authenticatedUser.slug ?? ""
      );
      if (!result.ok) {
        throw result;
      }
      await invalidateReservationQueries(queryClient);
      await messageState?.setMessageState("completed", {
        data: { scheduleId: schedule.id }
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not move the reservation."));
    }
  };

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      <div className={"flex min-w-[320px] flex-col gap-3"}>
        <div className={"grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"}>
          <span className={"text-neutral-600"}>From</span>
          <span>
            <span className={"font-medium"}>{schedule.resource?.name}</span> ·{" "}
            {from}
          </span>
          <span className={"text-neutral-600"}>To</span>
          <span>
            <span className={"font-medium"}>{resource?.name}</span> · {to}
          </span>
          {rooms && (
            <>
              <span className={"text-neutral-600"}>Seats</span>
              <span>
                {rooms.fromSeats} → {rooms.toSeats}
              </span>
              {(rooms.fromAmenities.length > 0 ||
                rooms.toAmenities.length > 0) && (
                <>
                  <span className={"text-neutral-600"}>Amenities</span>
                  <span>
                    {rooms.fromAmenities.join(", ") || "None"} →{" "}
                    {rooms.toAmenities.join(", ") || "None"}
                  </span>
                </>
              )}
            </>
          )}
        </div>
        {resource?.office_id && resource.parent_id && (
          <div className={"rounded-xl bg-neutral-50 p-2"}>
            <MapWithMiniOfficePhoto
              resourceId={resource.id}
              officeId={resource.office_id}
              parentId={resource.parent_id}
              startDate={moveMapDay(output.start, schedule.start, timeZone)}
              resourceImage={undefined}
              showMiniMap={false}
            />
          </div>
        )}
        {isDone ? (
          <div className={"flex items-center gap-2"}>
            <CheckIcon className={"size-6 text-green-500"} />
            Reservation moved.
          </div>
        ) : halfTimes ? (
          <p className={"text-red-600 text-sm"}>
            Only a new start or end time was proposed. Ask tessa again with both
            to move this reservation.
          </p>
        ) : (
          <Button isLoading={isMoving} onClick={move}>
            Move
          </Button>
        )}
      </div>
    </ChatBotToolCard>
  );
};
